import { Link, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Heart, PenLine, Bookmark, BookMarked, LineChart, Users, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds/app";
import { cn } from "@/lib/utils";

const links = [
  { to: "/home", label: "Home", icon: BookOpen, testid: NAV.home },
  { to: "/mood", label: "Mood", icon: Heart, testid: NAV.moodCheckin },
  { to: "/journal", label: "Journal", icon: PenLine, testid: NAV.journal },
  { to: "/library", label: "Library", icon: Bookmark, testid: NAV.library },
  { to: "/reading-list", label: "Reading list", icon: BookMarked, testid: NAV.readingList },
  { to: "/timeline", label: "Timeline", icon: LineChart, testid: NAV.timeline },
  { to: "/community", label: "Reflections", icon: Users, testid: NAV.community },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: NAV.settings },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="sticky top-0 z-40 bg-[#FDFBF7] border-b border-[#E8E3D5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
          <Link to="/home" data-testid={NAV.logo} className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="Bibliotherapy Companion"
              className="w-11 h-11 object-contain"
            />
            <div className="leading-tight">
              <div className="font-serif-display text-[#2C2520] text-lg">Bibliotherapy</div>
              <div className="label-caps text-[0.6rem]">Companion</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map(({ to, label, icon: Icon, testid }) => {
              const active = location.pathname === to || (to !== "/home" && location.pathname.startsWith(to));
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={cn(
                    "px-3.5 py-2 rounded-[10px] text-sm flex items-center gap-2 transition-colors duration-200",
                    active
                      ? "bg-[#4A5D4E] text-white"
                      : "text-[#2C2520]/75 hover:bg-[#F5F2EA] hover:text-[#2C2520]"
                  )}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <span className="text-sm text-[#2C2520]/60">{user?.name}</span>
            <button
              data-testid={NAV.logout}
              onClick={handleLogout}
              className="p-2 rounded-[10px] text-[#2C2520]/70 hover:bg-[#F5F2EA] hover:text-[#2C2520] transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          <button
            className="lg:hidden p-2 rounded-[10px] text-[#2C2520]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#E8E3D5] bg-[#FDFBF7]">
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map(({ to, label, icon: Icon, testid }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`${testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 rounded-[10px] text-sm text-[#2C2520] hover:bg-[#F5F2EA] flex items-center gap-2.5"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {label}
                </Link>
              ))}
              <button
                data-testid={`${NAV.logout}-mobile`}
                onClick={handleLogout}
                className="px-3 py-2.5 rounded-[10px] text-sm text-[#2C2520] hover:bg-[#F5F2EA] flex items-center gap-2.5 text-left"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">{children}</main>
    </div>
  );
}
